import { useState } from 'react'
import { Plus } from 'lucide-react'
import { Alert, AlertDescription, Badge, Spinner } from '@/components/ui'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui'
import { Button } from '@/components/ui'
import { useToast } from '@/components/ui'
import { usePersonaToolBindings, useSetToolBinding } from '@/hooks/useToolBindings'
import { useCapabilities } from '@/hooks/useCapabilities'
import { useAdapters } from '@/hooks/useAdapters'
import { useCredentialSetsForService } from '@/hooks/useCredentialSetsForService'
import { ApiError } from '@/services/apiClient'
import type { Adapter, Capability, ResolvedToolBindingEntry } from '@/types/toolFramework'

interface PersonaToolBindingsProps {
  personaId: string
}

function kindLabel(kind: string): string {
  if (kind === 'in_process') return 'Native'
  if (kind === 'mcp') return 'MCP'
  return kind
}

interface BindingErrorEntry {
  capability_id: string
  reason: string
}

function reasonToMessage(reason: string): string {
  switch (reason) {
    case 'credential_set_id_missing':
      return 'Credential set required'
    case 'credential_set_not_found':
      return 'Selected credential set was deleted'
    case 'credential_set_wrong_service':
      return 'Selected set does not contain the required service'
    default:
      return reason
  }
}

/**
 * Parses a 422 ApiError into a list of inline binding errors.
 * Returns null if the error isn't a structured bundle-validation response.
 */
function parseBindingErrors(err: unknown): BindingErrorEntry[] | null {
  if (!(err instanceof ApiError)) return null
  const detail = err.detail as { errors?: unknown } | null | undefined
  if (!detail || !Array.isArray(detail.errors)) return null
  return detail.errors.filter(
    (e): e is BindingErrorEntry =>
      typeof e === 'object' &&
      e !== null &&
      typeof (e as BindingErrorEntry).capability_id === 'string' &&
      typeof (e as BindingErrorEntry).reason === 'string',
  )
}

interface CredentialSetPickerProps {
  capabilityId: string
  service: string
  currentSetId: string | null
  onSave: (newSetId: string) => void
  disabled?: boolean
}

function CredentialSetPicker({
  capabilityId,
  service,
  currentSetId,
  onSave,
  disabled = false,
}: CredentialSetPickerProps) {
  const { data: sets = [], isLoading } = useCredentialSetsForService(service)

  if (isLoading) {
    return (
      <p className="text-xs text-muted-foreground">
        Loading credential sets…
      </p>
    )
  }

  if (sets.length === 0) {
    return (
      <p className="text-xs text-warning-strong">
        No credential sets configured for {service} — create one in Admin →
        Credential Sets.
      </p>
    )
  }

  const inputId = `credential-set-picker-${capabilityId}`

  return (
    <div className="flex items-center gap-2 text-xs">
      <label
        htmlFor={inputId}
        className="text-foreground"
      >
        Credential set:
      </label>
      <select
        id={inputId}
        aria-label="Credential set"
        value={currentSetId ?? ''}
        disabled={disabled}
        onChange={(e) => onSave(e.target.value)}
        className="rounded-md border border-border bg-background px-2 py-1"
      >
        <option value="" disabled>
          — Select credential set —
        </option>
        {sets.map((s) => (
          <option key={s.id} value={s.id}>
            {s.name} ({s.entry_count}{' '}
            {s.entry_count === 1 ? 'entry' : 'entries'})
          </option>
        ))}
      </select>
    </div>
  )
}

interface BindingRowProps {
  binding: ResolvedToolBindingEntry
  capability: Capability | undefined
  adapters: Adapter[]
  personaId: string
}

function BindingRow({ binding, capability, adapters, personaId }: BindingRowProps) {
  const { addToast } = useToast()
  const setBinding = useSetToolBinding()
  const [inlineErrors, setInlineErrors] = useState<BindingErrorEntry[]>([])
  const compatibleAdapters = adapters.filter((a) =>
    a.implements.includes(binding.capability_id),
  )
  const isPending = setBinding.isPending
  const requiresCreds = Boolean(capability?.requires_credentials)
  const service = capability?.service ?? ''

  function handleMutate(
    payload: { adapter_id: string; credential_set_id: string | null },
  ) {
    setInlineErrors([])
    setBinding.mutate(
      {
        personaId,
        capabilityId: binding.capability_id,
        ...payload,
      },
      {
        onSuccess: () => addToast({ type: 'success', title: 'Binding saved' }),
        onError: (err) => {
          const parsed = parseBindingErrors(err)
          if (parsed) {
            setInlineErrors(parsed)
            return
          }
          addToast({
            type: 'error',
            title: 'Failed to save binding',
            description: err instanceof Error ? err.message : undefined,
          })
        },
      },
    )
  }

  function handleChange(adapterId: string) {
    handleMutate({
      adapter_id: adapterId,
      credential_set_id: binding.credential_set_id ?? null,
    })
  }

  function handleCredentialSetChange(newSetId: string) {
    handleMutate({
      adapter_id: binding.adapter_id,
      credential_set_id: newSetId,
    })
  }

  const rowErrors = inlineErrors.filter(
    (e) => e.capability_id === binding.capability_id,
  )

  return (
    <div className="rounded-lg border border-border p-4 space-y-3">
      <div className="flex items-start justify-between gap-2">
        <span className="font-mono text-sm font-medium text-foreground break-all">
          {binding.capability_id}
        </span>
        {capability && (
          <Badge variant="secondary" className="shrink-0">
            {capability.category}
          </Badge>
        )}
      </div>

      {capability?.description && (
        <p className="text-sm text-muted-foreground">
          {capability.description}
        </p>
      )}

      {compatibleAdapters.length === 0 ? (
        <p className="text-sm text-muted-foreground italic">
          No registered adapters implement this capability.
        </p>
      ) : (
        <div className="space-y-2" role="radiogroup" aria-label={`Adapter for ${binding.capability_id}`}>
          {compatibleAdapters.map((adapter) => {
            const isSelected = adapter.id === binding.adapter_id
            const rowId = `binding-${binding.capability_id}-${adapter.id}`
            return (
              <label
                key={adapter.id}
                htmlFor={rowId}
                className={`flex items-center gap-3 rounded-md border p-3 cursor-pointer transition-colors ${
                  isSelected
                    ? 'border-primary bg-primary/10'
                    : 'border-border hover:border-muted-foreground/40'
                } ${isPending ? 'opacity-60 pointer-events-none' : ''}`}
              >
                <input
                  type="radio"
                  id={rowId}
                  name={`adapter-${personaId}-${binding.capability_id}`}
                  value={adapter.id}
                  checked={isSelected}
                  disabled={isPending}
                  onChange={() => handleChange(adapter.id)}
                  className="accent-primary"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-foreground">
                      {adapter.display_name}
                    </span>
                    <Badge variant="outline" className="text-xs">
                      {kindLabel(adapter.kind)}
                    </Badge>
                  </div>
                  <span className="font-mono text-xs text-muted-foreground">
                    {adapter.id}
                  </span>
                </div>
                {isPending && isSelected && (
                  <Spinner size="sm" className="shrink-0" />
                )}
              </label>
            )
          })}
        </div>
      )}

      {requiresCreds && service ? (
        <CredentialSetPicker
          capabilityId={binding.capability_id}
          service={service}
          currentSetId={binding.credential_set_id}
          onSave={handleCredentialSetChange}
          disabled={isPending}
        />
      ) : requiresCreds ? (
        <p className="text-xs text-muted-foreground">
          {binding.credential_set_id
            ? `Credential set: ${binding.credential_set_id}`
            : 'Uses default credential set'}
        </p>
      ) : (
        <p className="text-xs text-muted-foreground">Built-in · no key needed</p>
      )}

      {rowErrors.length > 0 && (
        <ul className="text-xs text-destructive space-y-1">
          {rowErrors.map((e, i) => (
            <li key={`${e.reason}-${i}`}>{reasonToMessage(e.reason)}</li>
          ))}
        </ul>
      )}
    </div>
  )
}

interface AddBindingPickerProps {
  personaId: string
  capabilities: Capability[]
  adapters: Adapter[]
  boundCapabilityIds: Set<string>
  onClose: () => void
}

function AddBindingPicker({
  personaId,
  capabilities,
  adapters,
  boundCapabilityIds,
  onClose,
}: AddBindingPickerProps) {
  const { addToast } = useToast()
  const setBinding = useSetToolBinding()
  const [selectedCapabilityId, setSelectedCapabilityId] = useState('')
  const [selectedAdapterId, setSelectedAdapterId] = useState('')
  const [selectedCredentialSetId, setSelectedCredentialSetId] = useState<
    string | null
  >(null)
  const [inlineErrors, setInlineErrors] = useState<BindingErrorEntry[]>([])

  const unboundCapabilities = capabilities.filter(
    (c) => !boundCapabilityIds.has(c.id),
  )

  const selectedCapability = capabilities.find(
    (c) => c.id === selectedCapabilityId,
  )
  const requiresCredentials = Boolean(selectedCapability?.requires_credentials)
  const service = selectedCapability?.service ?? ''

  const compatibleAdapters =
    selectedCapabilityId
      ? adapters.filter((a) => a.implements.includes(selectedCapabilityId))
      : []

  const { data: credentialSets = [], isLoading: isLoadingSets } =
    useCredentialSetsForService(
      requiresCredentials && selectedAdapterId ? service : '',
    )

  function handleCapabilityChange(capId: string) {
    setSelectedCapabilityId(capId)
    setSelectedAdapterId('')
    setSelectedCredentialSetId(null)
    setInlineErrors([])
  }

  function handleAdapterChange(adapterId: string) {
    setSelectedAdapterId(adapterId)
    setSelectedCredentialSetId(null)
    setInlineErrors([])
  }

  const canSave = Boolean(
    selectedCapabilityId &&
      selectedAdapterId &&
      (!requiresCredentials || selectedCredentialSetId !== null),
  )

  function handleSave() {
    if (!canSave) return
    setInlineErrors([])
    setBinding.mutate(
      {
        personaId,
        capabilityId: selectedCapabilityId,
        adapter_id: selectedAdapterId,
        credential_set_id: selectedCredentialSetId,
      },
      {
        onSuccess: () => {
          addToast({ type: 'success', title: 'Binding saved' })
          onClose()
        },
        onError: (err) => {
          const parsed = parseBindingErrors(err)
          if (parsed) {
            setInlineErrors(parsed)
            return
          }
          addToast({
            type: 'error',
            title: 'Failed to save binding',
            description: err instanceof Error ? err.message : undefined,
          })
        },
      },
    )
  }

  if (unboundCapabilities.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border p-4 text-sm text-muted-foreground">
        All available capabilities are already bound.
        <Button variant="ghost" size="sm" className="ml-2" onClick={onClose}>
          Cancel
        </Button>
      </div>
    )
  }

  return (
    <div className="rounded-lg border border-dashed border-primary/40 p-4 space-y-3">
      <p className="text-sm font-medium text-foreground">Add binding</p>

      <div className="space-y-2">
        <label className="text-xs text-muted-foreground">Capability</label>
        <Select value={selectedCapabilityId} onValueChange={handleCapabilityChange}>
          <SelectTrigger>
            <SelectValue placeholder="Choose a capability…" />
          </SelectTrigger>
          <SelectContent>
            {unboundCapabilities.map((cap) => (
              <SelectItem key={cap.id} value={cap.id}>
                {cap.id}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selectedCapabilityId && (
        <div className="space-y-2">
          <label className="text-xs text-muted-foreground">Adapter</label>
          {compatibleAdapters.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">
              No adapters implement this capability.
            </p>
          ) : (
            <Select value={selectedAdapterId} onValueChange={handleAdapterChange}>
              <SelectTrigger>
                <SelectValue placeholder="Choose an adapter…" />
              </SelectTrigger>
              <SelectContent>
                {compatibleAdapters.map((adapter) => (
                  <SelectItem key={adapter.id} value={adapter.id}>
                    {adapter.display_name} ({adapter.id})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      )}

      {requiresCredentials && selectedAdapterId && (
        <div className="space-y-2">
          <label
            htmlFor="add-binding-credential-set"
            className="text-xs text-muted-foreground"
          >
            Credential set
          </label>
          {isLoadingSets ? (
            <p className="text-xs text-muted-foreground">
              Loading credential sets…
            </p>
          ) : credentialSets.length === 0 ? (
            <p className="text-xs text-warning-strong">
              No credential sets for {service}. Create one in Admin →
              Credential Sets.
            </p>
          ) : (
            <select
              id="add-binding-credential-set"
              aria-label="Credential set"
              value={selectedCredentialSetId ?? ''}
              onChange={(e) => setSelectedCredentialSetId(e.target.value)}
              className="w-full rounded-md border border-border bg-background px-2 py-1 text-sm"
            >
              <option value="" disabled>
                — Select credential set —
              </option>
              {credentialSets.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} ({s.entry_count}{' '}
                  {s.entry_count === 1 ? 'entry' : 'entries'})
                </option>
              ))}
            </select>
          )}
        </div>
      )}

      {inlineErrors.length > 0 && (
        <ul className="text-xs text-destructive space-y-1">
          {inlineErrors.map((e, i) => (
            <li key={`${e.capability_id}-${e.reason}-${i}`}>
              {reasonToMessage(e.reason)}
            </li>
          ))}
        </ul>
      )}

      <div className="flex gap-2 pt-1">
        <Button
          size="sm"
          onClick={handleSave}
          disabled={!canSave}
          isLoading={setBinding.isPending}
        >
          Save
        </Button>
        <Button variant="ghost" size="sm" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  )
}

export function PersonaToolBindings({ personaId }: PersonaToolBindingsProps) {
  const [showPicker, setShowPicker] = useState(false)

  const {
    data: bindingsData,
    isLoading: isLoadingBindings,
    error: bindingsError,
  } = usePersonaToolBindings(personaId)

  const { data: capabilities, isLoading: isLoadingCaps, error: capsError } = useCapabilities()
  const { data: adapters, isLoading: isLoadingAdapters, error: adaptersError } = useAdapters()

  const isLoading = isLoadingBindings || isLoadingCaps || isLoadingAdapters
  const error = bindingsError ?? capsError ?? adaptersError

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          {error instanceof Error ? error.message : 'Failed to load tool bindings'}
        </AlertDescription>
      </Alert>
    )
  }

  const bindings = bindingsData?.bindings ?? []
  const caps = capabilities ?? []
  const adapterList = adapters ?? []
  const boundCapabilityIds = new Set(bindings.map((b) => b.capability_id))

  const capabilityMap = new Map<string, Capability>(caps.map((c) => [c.id, c]))

  return (
    <div className="space-y-4">
      {bindings.length === 0 && !showPicker && (
        <div className="text-sm text-muted-foreground py-4">
          <p className="mb-3">
            No tool bindings configured for this persona. Add a binding to
            enable the persona to call external capabilities (e.g. creating
            payment links) via a specific adapter.
          </p>
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Plus className="w-4 h-4" />}
            onClick={() => setShowPicker(true)}
          >
            Add binding
          </Button>
        </div>
      )}

      {bindings.map((binding) => (
        <BindingRow
          key={binding.capability_id}
          binding={binding}
          capability={capabilityMap.get(binding.capability_id)}
          adapters={adapterList}
          personaId={personaId}
        />
      ))}

      {bindings.length > 0 && !showPicker && (
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<Plus className="w-4 h-4" />}
          onClick={() => setShowPicker(true)}
        >
          Add binding
        </Button>
      )}

      {showPicker && (
        <AddBindingPicker
          personaId={personaId}
          capabilities={caps}
          adapters={adapterList}
          boundCapabilityIds={boundCapabilityIds}
          onClose={() => setShowPicker(false)}
        />
      )}
    </div>
  )
}
